import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, PlayCircle, Info } from 'lucide-react';
import { useLabStatus, useLabPrograms, useUpdateLab } from '../../hooks/useLabAPI';
import PageHeader from '../../components/PageHeader';
import { quantizeDb, formatDb } from '@/utils/tinnitusDisplay';

/**
 * Lab Plan Page - Vue d'ensemble des modes Notch
 * 
 * BUILD 52.5: Uses centralized tinnitusDisplay utilities for consistent dB display
 * 
 * DESIGN PREMIUM & APAISANT:
 * - Gain affiché sans signe "+" agressif, mais avec quantification 0.5 dB
 * - Couleurs désaturées pour le gain
 * - Bouton "Suspendre" discret au lieu de "Désactiver"
 * - Bandeau sécurité apaisant
 * - Tooltip explicatif sur badge "Recommandé"
 */

const getLang = () => localStorage.getItem('hearen_lang') || 'fr';
const formatBand = () => '½ octave';

// BUILD 66: Format gain using qualitative labels instead of numeric dB values
// - Positive values (stimulation): "Progressive stimulation"
// - Negative values (attenuation): "Conservative attenuation"
const formatGain = (value, lang = getLang()) => {
  const quantized = quantizeDb(value);
  
  if (quantized > 0) {
    // Positive = stimulation (Inverse Notch)
    return lang === 'en' ? 'Progressive stimulation' : 'Stimulation progressive';
  } else if (quantized < 0) {
    // Negative = attenuation (Classic Notch)
    return lang === 'en' ? 'Conservative attenuation' : 'Atténuation conservatrice';
  } else {
    // Zero = neutral
    return lang === 'en' ? 'Neutral' : 'Neutre';
  }
};

// Tooltip content for "Recommended" badge
const recommendedTooltip = {
  fr: {
    title: 'Pourquoi recommandé ?',
    text: "L'Inverse Notch applique une stimulation douce et progressive autour de votre fréquence d'acouphène.\n\nCette approche vise à favoriser l'adaptation auditive tout en respectant des limites de sécurité strictes.\n\nElle est proposée par défaut car elle est généralement mieux tolérée par les utilisateurs, tout en restant optionnelle."
  },
  en: {
    title: 'Why recommended?',
    text: "Inverse Notch applies gentle, progressive stimulation around your tinnitus frequency.\n\nThis approach aims to promote auditory adaptation while respecting strict safety limits.\n\nIt is offered by default as it is generally better tolerated by users, while remaining optional."
  }
};

const copy = {
  fr: {
    viewPlan: 'Voir le plan complet',
    // Calming safety message (technical -1 dBFS hidden)
    safetyMessage: 'Arrêt immédiat toujours disponible · Niveau sécurisé',
    safetyTooltip: 'Peak limité à −1 dBFS',
    generalGuideline: 'Cible recommandée : 60-120 min/jour · 6-12 semaines',
    classicTitle: 'Classic Notch',
    classicSubtitle: 'Atténuation ciblée (mode conservateur)',
    inverseTitle: 'Inverse Notch',
    inverseSubtitle: 'Stimulation douce et progressive',
    recommended: 'Recommandé',
    beta: 'Bêta',
    frequency: 'Fréquence (fₜ)',
    band: 'Bande',
    correction: 'Correction',
    active: 'Actif',
    activate: 'Activer',
    suspend: 'Suspendre', // Changed from "Désactiver"
    switchToRecommended: '→ Mode recommandé',
    pitchTitle: 'Affinez votre fréquence',
    pitchDesc: 'Affinez votre fréquence d\'acouphène (fₜ) avec notre outil guidé',
    startTest: 'Démarrer le test',
    saved: 'Sauvegardé',
    error: 'Erreur',
    loading: 'Chargement...'
  },
  en: {
    viewPlan: 'View full plan',
    safetyMessage: 'Immediate stop always available · Safe level',
    safetyTooltip: 'Peak limited to −1 dBFS',
    generalGuideline: 'Recommended target: 60-120 min/day · 6-12 weeks',
    classicTitle: 'Classic Notch',
    classicSubtitle: 'Targeted attenuation (conservative mode)',
    inverseTitle: 'Inverse Notch',
    inverseSubtitle: 'Gentle and progressive stimulation',
    recommended: 'Recommended',
    beta: 'Beta',
    frequency: 'Frequency (fₜ)',
    band: 'Band',
    correction: 'Correction',
    active: 'Active',
    activate: 'Activate',
    suspend: 'Suspend', // Changed from "Deactivate"
    switchToRecommended: '→ Recommended mode',
    pitchTitle: 'Refine your frequency',
    pitchDesc: 'Refine your tinnitus frequency (fₜ) with our guided tool',
    startTest: 'Start test',
    saved: 'Saved',
    error: 'Error',
    loading: 'Loading...'
  }
};

const LabPlan = ({ t: propT }) => {
  const navigate = useNavigate();
  const { status, loading: statusLoading } = useLabStatus();
  const { programs, loading: programsLoading, refetch: refetchPrograms } = useLabPrograms();
  const { updateLab, updating } = useUpdateLab();

  const [toast, setToast] = useState(null);
  const [showSafetyTooltip, setShowSafetyTooltip] = useState(false);
  const [showRecommendedTooltip, setShowRecommendedTooltip] = useState(false);
  const recommendedTooltipRef = useRef(null);
  const t = copy[getLang()] || copy.fr;
  const tooltipContent = recommendedTooltip[getLang()] || recommendedTooltip.fr;

  // Close tooltip on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (recommendedTooltipRef.current && !recommendedTooltipRef.current.contains(event.target)) {
        setShowRecommendedTooltip(false);
      }
    };
    if (showRecommendedTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showRecommendedTooltip]);

  const showToastMsg = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Auto-activate Inverse Notch if no program is active (default behavior)
  useEffect(() => {
    if (!programsLoading && programs && !programs.active_program && status?.consent_accepted) {
      updateLab({ active_program: 'inverse_notch' }).then(() => {
        refetchPrograms();
      });
    }
  }, [programsLoading, programs?.active_program, status?.consent_accepted]);

  const handleActivateProgram = async (programName) => {
    if (!status?.consent_accepted) {
      navigate('/settings/lab');
      return;
    }
    try {
      await updateLab({ active_program: programName });
      showToastMsg(t.saved);
      refetchPrograms();
    } catch (error) {
      showToastMsg(t.error, 'error');
    }
  };

  const handleSuspend = async () => {
    try {
      await updateLab({ active_program: null });
      showToastMsg(t.saved);
      refetchPrograms();
    } catch (error) {
      showToastMsg(t.error, 'error');
    }
  };

  if (statusLoading || programsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 px-8 pb-40">
        <div className="max-w-6xl mx-auto">
          <PageHeader t={propT} />
          <div className="text-center py-12">{t.loading}</div>
        </div>
      </div>
    );
  }

  const activeProgram = programs?.active_program;
  const isClassicActive = activeProgram === 'notch';
  const isInverseActive = activeProgram === 'inverse_notch';

  return (
    <div className="min-h-screen bg-gray-50 pt-20 px-8 pb-40">
      <div className="max-w-6xl mx-auto">
        <PageHeader t={propT} />

        <h1 className="text-3xl font-bold text-gray-900 mb-6">{t.viewPlan}</h1>

        {/* Toast */}
        {toast && (
          <Alert className={`mb-4 ${toast.type === 'error' ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'}`}>
            <AlertDescription className={toast.type === 'error' ? 'text-red-800' : 'text-green-800'}>
              {toast.message}
            </AlertDescription>
          </Alert>
        )}

        {/* Safety Banner - Calming design */}
        <div 
          className="mb-4 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-slate-700 to-slate-800 rounded-lg relative"
          onMouseEnter={() => setShowSafetyTooltip(true)}
          onMouseLeave={() => setShowSafetyTooltip(false)}
        >
          <Shield className="h-4 w-4 text-emerald-400" />
          <span className="text-white/90 text-sm font-medium">{t.safetyMessage}</span>
          <Info className="h-3.5 w-3.5 text-white/50 cursor-help" />
          {/* Tooltip for technical info */}
          {showSafetyTooltip && (
            <div className="absolute top-full mt-2 px-3 py-1.5 bg-slate-900 text-white/80 text-xs rounded shadow-lg z-10">
              {t.safetyTooltip}
            </div>
          )}
        </div>

        {/* General Guideline */}
        <div className="mb-6 text-center text-gray-500 text-sm">
          {t.generalGuideline}
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          
          {/* ==================== INVERSE NOTCH (FIRST - RECOMMENDED) ==================== */}
          <Card className={`transition-all ${isInverseActive ? 'ring-2 ring-amber-400/60 shadow-lg' : 'border-2 border-amber-100'}`}>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2.5 flex-wrap">
                <span className="text-xl font-semibold">{t.inverseTitle}</span>
                {/* Badges - hierarchical: Recommandé > Bêta > Actif */}
                {/* Recommended badge with info tooltip */}
                <div className="relative inline-flex items-center" ref={recommendedTooltipRef}>
                  <span className="px-2.5 py-1 bg-blue-600 text-white text-xs font-semibold rounded-full">
                    {t.recommended}
                  </span>
                  <button
                    onClick={() => setShowRecommendedTooltip(!showRecommendedTooltip)}
                    className="ml-1 p-0.5 text-blue-500 hover:text-blue-600 focus:outline-none"
                    aria-label="Info"
                  >
                    <Info className="h-3.5 w-3.5" />
                  </button>
                  {/* Tooltip */}
                  {showRecommendedTooltip && (
                    <div className="absolute left-0 top-full mt-2 w-80 p-4 bg-white rounded-xl shadow-2xl border border-gray-200 z-50">
                      <p className="text-base font-bold text-blue-700 mb-3">{tooltipContent.title}</p>
                      <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{tooltipContent.text}</p>
                    </div>
                  )}
                </div>
                <span className="px-2.5 py-1 bg-amber-500/80 text-white text-xs font-semibold rounded-full">
                  {t.beta}
                </span>
                {isInverseActive && (
                  <span className="px-2.5 py-1 bg-emerald-500 text-white text-xs font-semibold rounded-full">
                    {t.active}
                  </span>
                )}
              </CardTitle>
              <CardDescription className="text-amber-700/80 font-medium mt-1">
                {t.inverseSubtitle}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {programs?.inverse_notch && (
                <div className="bg-gradient-to-br from-amber-500/90 to-amber-600/90 rounded-xl p-4 space-y-3 shadow">
                  <div className="flex justify-between text-sm">
                    <span className="text-amber-100/80">{t.frequency}:</span>
                    <span className="font-bold text-white text-lg">{programs.inverse_notch.f_t?.toLocaleString()} Hz</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-amber-100/80">{t.band}:</span>
                    <span className="font-semibold text-white">{formatBand()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-amber-100/80">{t.correction}:</span>
                    {/* Desaturated gain display without aggressive "+" */}
                    <span className="font-semibold text-amber-100 text-lg">{formatGain(programs.inverse_notch.gain_db)}</span>
                  </div>
                </div>
              )}

              {/* Button: Active state = discrete "Suspend", Inactive = prominent "Activate" */}
              {isInverseActive ? (
                <Button
                  onClick={handleSuspend}
                  disabled={updating}
                  variant="ghost"
                  className="w-full h-10 text-sm text-amber-700/70 hover:text-amber-800 hover:bg-amber-50 border border-amber-200/50"
                  data-testid="suspend-inverse-notch-btn"
                >
                  {t.suspend}
                </Button>
              ) : (
                <Button
                  onClick={() => handleActivateProgram('inverse_notch')}
                  disabled={updating}
                  className="w-full h-12 text-base bg-amber-500 hover:bg-amber-600 shadow"
                  data-testid="activate-inverse-notch-btn"
                >
                  <PlayCircle size={18} className="mr-2" />
                  {t.activate}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* ==================== CLASSIC NOTCH (SECOND - CONSERVATIVE) ==================== */}
          <Card className={`transition-all ${isClassicActive ? 'ring-2 ring-slate-400/60 shadow-lg' : 'border border-gray-200 opacity-85'}`}>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2.5 flex-wrap">
                <span className="text-xl font-semibold">{t.classicTitle}</span>
                {isClassicActive && (
                  <span className="px-2.5 py-1 bg-emerald-500 text-white text-xs font-semibold rounded-full">
                    {t.active}
                  </span>
                )}
              </CardTitle>
              <CardDescription className="text-slate-500 font-medium mt-1">
                {t.classicSubtitle}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {programs?.notch && (
                <div className="bg-gradient-to-br from-slate-500/90 to-slate-600/90 rounded-xl p-4 space-y-3 shadow">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-200/80">{t.frequency}:</span>
                    <span className="font-bold text-white text-lg">{programs.notch.f_t?.toLocaleString()} Hz</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-200/80">{t.band}:</span>
                    <span className="font-semibold text-white">{formatBand()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-200/80">{t.correction}:</span>
                    <span className="font-semibold text-slate-200 text-lg">{formatGain(programs.notch.gain_db)}</span>
                  </div>
                </div>
              )}

              {isClassicActive ? (
                <div className="space-y-2">
                  <Button
                    onClick={() => handleActivateProgram('inverse_notch')}
                    disabled={updating}
                    className="w-full h-11 text-sm bg-amber-500 hover:bg-amber-600"
                  >
                    {t.switchToRecommended}
                  </Button>
                  <Button
                    onClick={handleSuspend}
                    disabled={updating}
                    variant="ghost"
                    className="w-full h-9 text-xs text-slate-500/70 hover:text-slate-600 hover:bg-slate-50 border border-slate-200/50"
                  >
                    {t.suspend}
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={() => handleActivateProgram('notch')}
                  disabled={updating}
                  variant="outline"
                  className="w-full h-11 text-sm border-slate-300 text-slate-600 hover:bg-slate-50"
                  data-testid="activate-notch-btn"
                >
                  <PlayCircle size={16} className="mr-2" />
                  {t.activate}
                </Button>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Pitch Matching CTA - REMOVED: Test should be done in "Hearing Tests" only */}
      </div>
    </div>
  );
};

export default LabPlan;
